import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowUpCircle, ArrowDownCircle, Search, PlayCircle, FileText, Loader2 } from 'lucide-react';
import { useSuccessWareOperations } from '@/lib/hooks/useIntegrationOperations';
import { IntegrationOperation } from '@/lib/repositories/IntegrationOperationsRepository';

const OperationCatalogPage = () => {
  const { data: operations = [], isLoading } = useSuccessWareOperations();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDirection, setFilterDirection] = useState<string>('all');
  const [filterFamily, setFilterFamily] = useState<string>('all');
  const [selectedOperation, setSelectedOperation] = useState<IntegrationOperation | null>(null);

  // Client-side filtering
  const filteredOperations = useMemo(() => {
    return operations.filter(op => {
      const matchesSearch = op.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           op.graphql_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           op.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDirection = filterDirection === 'all' || op.direction === filterDirection;
      const matchesFamily = filterFamily === 'all' || op.family === filterFamily;
      return matchesSearch && matchesDirection && matchesFamily;
    });
  }, [operations, searchTerm, filterDirection, filterFamily]);

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
          <Link to="/integrations" className="hover:text-white">Integrations</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">Operation Catalog</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Operation Catalog</h1>
        <p className="text-slate-400">
          Browse all SuccessWare API operations, sample queries, and test them
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search operations..."
              className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        <select
          value={filterDirection}
          onChange={(e) => setFilterDirection(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-2 text-white"
        >
          <option value="all">All Directions</option>
          <option value="push">Push (Mutations)</option>
          <option value="pull">Pull (Queries)</option>
        </select>

        <select
          value={filterFamily}
          onChange={(e) => setFilterFamily(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-2 text-white"
        >
          <option value="all">All Families</option>
          <option value="core">Core</option>
          <option value="bookit">BookIt</option>
        </select>
      </div>

      {/* Operations Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Operation</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">GraphQL Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Family</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Input Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredOperations.map((op) => (
                <tr key={op.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-white">{op.key.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-slate-400 mt-1">{op.description}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-sm text-blue-400">{op.graphql_name}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      op.direction === 'push'
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {op.direction === 'push' ? (
                        <><ArrowUpCircle className="w-3 h-3" /> Push</>
                      ) : (
                        <><ArrowDownCircle className="w-3 h-3" /> Pull</>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      op.family === 'bookit'
                        ? 'bg-purple-500/20 text-purple-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {op.family}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs text-slate-400">{op.input_type || 'None'}</code>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedOperation(op)}
                      className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                    >
                      <FileText className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredOperations.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              No operations found matching your filters
            </div>
          )}
        </div>
      )}

      {/* Operation Detail Drawer */}
      {selectedOperation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end">
          <div className="bg-slate-900 w-full max-w-3xl h-full overflow-y-auto border-l border-slate-800">
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{selectedOperation.key.replace(/_/g, ' ')}</h2>
                <p className="text-slate-400">{selectedOperation.description}</p>
              </div>
              <button
                onClick={() => setSelectedOperation(null)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400">GraphQL Operation</label>
                  <code className="block text-blue-400 mt-1">{selectedOperation.graphql_name}</code>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Input Type</label>
                  <code className="block text-slate-300 mt-1">{selectedOperation.input_type || 'None'}</code>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Direction</label>
                  <span className="block text-slate-300 mt-1 capitalize">{selectedOperation.direction}</span>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Family</label>
                  <span className="block text-slate-300 mt-1 capitalize">{selectedOperation.family}</span>
                </div>
              </div>

              {/* Sample Query */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Sample Query/Mutation</label>
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
                  {selectedOperation.sample_query}
                </pre>
              </div>

              {/* Sample Variables */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Sample Variables</label>
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
                  {JSON.stringify(selectedOperation.sample_variables_json, null, 2)}
                </pre>
              </div>

              {/* Run Button */}
              <Link
                to={`/integrations/successware/runner?operation=${selectedOperation.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <PlayCircle className="w-4 h-4" />
                Test in Runner
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationCatalogPage;
